import { RESULT_EFFECTS, TAG_COLORS, type ResultEffect, type TagColor } from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';

import { TypeIcon } from '../../components/activities/type-style.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Field, Input, Select } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { REMINDER_OPTIONS, useActivityTypes, type ActivityType } from '../../lib/activities-api.ts';
import { api, errorMessage } from '../../lib/api.ts';
import { useToast } from '../../lib/toast.tsx';

const COLOR_NAMES: Record<TagColor, string> = {
  slate: 'Gris',
  red: 'Rojo',
  orange: 'Naranja',
  amber: 'Ámbar',
  green: 'Verde',
  teal: 'Turquesa',
  blue: 'Azul',
  violet: 'Violeta',
  pink: 'Rosa',
};

/** HU-AGE-01: activity types (color, default reminder) and their results. */
export function ActivityTypesPage() {
  const types = useActivityTypes(true);
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Tipos de actividad</h1>
        <p className="text-sm text-muted-foreground">
          Los tipos y resultados que se eligen en la agenda. Los resultados de las visitas marcan la
          propiedad en el negocio. Los que ya se usaron no se borran: se desactivan.
        </p>
      </div>
      {types.isPending ? (
        <Loading />
      ) : types.error ? (
        <Alert tone="danger">{errorMessage(types.error)}</Alert>
      ) : (
        <>
          {types.data.map((t) => (
            <TypeCard key={t.id} type={t} />
          ))}
          <NewType />
        </>
      )}
    </div>
  );
}

function useSave() {
  const client = useQueryClient();
  const notify = useToast();
  return async (path: string, method: 'POST' | 'PATCH', body: unknown, done?: string) => {
    try {
      await api(path, { method, body });
      await client.invalidateQueries({ queryKey: ['activity-types'] });
      if (done) notify(done, 'success');
      return true;
    } catch (error) {
      notify(errorMessage(error), 'error');
      return false;
    }
  };
}

const reminderValue = (minutes: number | null) => (minutes === null ? 'none' : String(minutes));

function TypeCard({ type }: { type: ActivityType }) {
  const save = useSave();
  const [name, setName] = useState(type.name);
  const [color, setColor] = useState<TagColor>(type.color);
  const [reminder, setReminder] = useState(reminderValue(type.defaultReminderMinutes));
  const [result, setResult] = useState('');
  const [effect, setEffect] = useState<ResultEffect>('none');
  const changed =
    name.trim() !== type.name ||
    color !== type.color ||
    reminder !== reminderValue(type.defaultReminderMinutes);
  const path = `/activity-types/${type.id}`;
  return (
    <Card className="space-y-4">
      <div className="flex items-center gap-3">
        <TypeIcon kind={type.kind} color={color} />
        <h2 className="font-semibold">{type.name}</h2>
        {!type.active && <Badge>Desactivado</Badge>}
      </div>
      <div className="grid items-end gap-3 sm:grid-cols-[1fr_10rem_12rem_auto]">
        <Field label="Nombre">
          {(a11y) => (
            <Input
              {...a11y}
              maxLength={40}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          )}
        </Field>
        <Field label="Color">
          {(a11y) => (
            <Select
              {...a11y}
              value={color}
              onChange={(e) => {
                setColor(e.target.value as TagColor);
              }}
            >
              {TAG_COLORS.map((c) => (
                <option key={c} value={c}>
                  {COLOR_NAMES[c]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Recordatorio">
          {(a11y) => (
            <Select
              {...a11y}
              value={reminder}
              onChange={(e) => {
                setReminder(e.target.value);
              }}
            >
              {REMINDER_OPTIONS.map(([value, label]) => (
                <option key={label} value={value === null ? 'none' : String(value)}>
                  {label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="flex gap-1">
          {changed && (
            <Button
              onClick={() =>
                void save(
                  path,
                  'PATCH',
                  {
                    name,
                    color,
                    defaultReminderMinutes: reminder === 'none' ? null : Number(reminder),
                  },
                  'Tipo guardado.',
                )
              }
            >
              Guardar
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={() => void save(path, 'PATCH', { active: !type.active })}
          >
            {type.active ? 'Desactivar' : 'Activar'}
          </Button>
        </div>
      </div>
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Resultados</h3>
        <ul className="divide-y divide-border text-sm">
          {type.results.map((r) => (
            <li key={r.id} className="flex min-h-11 items-center justify-between gap-2">
              <span className={r.active ? '' : 'text-muted-foreground line-through'}>
                {r.name}
                {r.effect !== 'none' && (
                  <span className="text-muted-foreground"> · {RESULT_EFFECTS[r.effect]}</span>
                )}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void save(`${path}/results/${r.id}`, 'PATCH', { active: !r.active })}
              >
                {r.active ? 'Desactivar' : 'Activar'}
              </Button>
            </li>
          ))}
        </ul>
        <form
          className="grid items-end gap-2 sm:grid-cols-[1fr_16rem_auto]"
          onSubmit={(e) => {
            e.preventDefault();
            void save(
              `${path}/results`,
              'POST',
              { name: result, effect },
              'Resultado agregado.',
            ).then((ok) => {
              if (ok) setResult('');
            });
          }}
        >
          <Field label={`Nuevo resultado de ${type.name}`}>
            {(a11y) => (
              <Input
                {...a11y}
                maxLength={60}
                value={result}
                onChange={(e) => {
                  setResult(e.target.value);
                }}
              />
            )}
          </Field>
          <Field label="Qué hace en el negocio">
            {(a11y) => (
              <Select
                {...a11y}
                value={effect}
                onChange={(e) => {
                  setEffect(e.target.value as ResultEffect);
                }}
              >
                {(Object.entries(RESULT_EFFECTS) as [ResultEffect, string][]).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Button type="submit" variant="secondary" disabled={!result.trim()}>
            <Plus /> Agregar
          </Button>
        </form>
      </div>
    </Card>
  );
}

function NewType() {
  const save = useSave();
  const [name, setName] = useState('');
  const [color, setColor] = useState<TagColor>('blue');
  return (
    <Card>
      <form
        className="grid items-end gap-2 sm:grid-cols-[1fr_10rem_auto]"
        onSubmit={(e) => {
          e.preventDefault();
          void save('/activity-types', 'POST', { name, color }, 'Tipo creado.').then((ok) => {
            if (ok) setName('');
          });
        }}
      >
        <Field label="Nuevo tipo">
          {(a11y) => (
            <Input
              {...a11y}
              maxLength={40}
              placeholder="Firma de contrato"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          )}
        </Field>
        <Field label="Color">
          {(a11y) => (
            <Select
              {...a11y}
              value={color}
              onChange={(e) => {
                setColor(e.target.value as TagColor);
              }}
            >
              {TAG_COLORS.map((c) => (
                <option key={c} value={c}>
                  {COLOR_NAMES[c]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Button type="submit" variant="secondary" disabled={!name.trim()}>
          <Plus /> Crear tipo
        </Button>
      </form>
    </Card>
  );
}
